import * as React from 'react';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import type {NavigationState} from '@react-navigation/native';
import {
  NotificationClickEvent,
  NotificationWillDisplayEvent,
  OneSignal,
  OSNotification,
} from 'react-native-onesignal';
import NavActions from 'src/actions/NavActions';
import AppNavigator from 'src/navigators/AppNavigator';
import type {RootRoutes} from 'src/AppRoutes';
import AppRoutes from 'src/AppRoutes';
import Localized from 'src/constants/AppStrings';
import Events from 'src/logging/Events';
import ModalSpinner from './dialogs/ModalSpinner';
import Styles from './Styles';
import ActionsFactory from 'src/actions/ActionsFactory';
import ScreenContext from './ScreenContext';
import type {NestedPushAfterLogin, ScreenProps} from 'src/types/Screen';
import {alertError, confirm, paymentIsCancelled} from './helpers/AlertHelper';
import {AccountFundingScreenProps} from './screens/funding/AccountFundingScreen';
import {getDescriber} from './descriptor/DescriptorType';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {getRewardProgramForTierStatus} from 'src/components/elements/rewards/rewardsUtil';

import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/fr-ca';
import 'moment/locale/fr-ch';
import 'moment/locale/it';
import 'moment/locale/it-ch';
import 'moment/locale/en-gb';
import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/en-ie';
import 'moment/locale/es';
import 'moment/locale/es-us';
import 'moment/locale/nl';
import 'moment/locale/nl-be';
import 'moment/locale/de';
import 'moment/locale/de-ch';
import 'moment/locale/sv';
import 'moment/locale/da';
import 'moment/locale/nb';
import 'moment/locale/fi';

import {
  AppState,
  BackHandler,
  Linking,
  NativeEventSubscription,
  Platform,
  StatusBar,
  StyleSheet,
  Text,
} from 'react-native';
import PersistentStore from 'src/services/PersistentStoreService';
import Settings from 'src/Settings';
import DBHelper from 'src/services/DBService';
import SyncHelper from 'src/services/SyncService';
import AccountStore from 'src/stores/AccountStore';
import {filterPositions} from './screens/inbox/InboxScreen';
import {connect} from 'react-redux';
import {AppDispatch, RootState} from 'src/redux/store';
import {hideSpinner, ISpinner} from 'src/redux/slices/screenSlice';
import FirebaseAnalytic from '../nativeModules/FirebaseAnalytic';
import SuccessModal from 'src/components/elements/SuccessModal';
import {PaymentCredentials} from '../types/serverTypes/Account';
import BuildTypeConstants from 'src/constants/BuildTypeConstants';
import AppLifecycleTracker from 'src/components/utils/AppLifecycleTracker';
import {RewardStatusModel} from 'src/types/RewardsModel';
import {authStore} from '../init';
import {migrateOldSessionIfNeeded} from '../nativeModules/Auth0';
import {tryRecoverSession} from './utils/TryRecoverSession';
import {endPaymentFunding} from '../redux/slices/accountSlice';
import {initializeInstana} from '../config/instana';
import AppNotificationsEventOpened from 'src/NotificationEvent';
import ErrorModal from 'src/components/elements/ErrorModal';
import CartToastView, {
  CartItemType,
} from 'src/components/elements/cart/CartToastView';
import Util from 'src/Util';
import {OneSignalAdapter} from './utils/Adapters/OneSignalAdapter';
import {handleOpenHomeDeepLink} from './utils/deepLinkHandler';

moment.locale([Settings.getLocale(), 'en']);
type AppStateType = {
  spinner: boolean;
  spinnerText: string;
  spinnerCancel: boolean;
  spinnerCallback?: () => void;
  spinnerCancelText: string;
  screenProps: ScreenProps;
  rootRoute: RootRoutes;
  showSuccessModal: boolean;
  modalDescription: string;
  modalDescriptionLine: number;
  modalIsAcceptSnack: boolean;
  modalIsMultipleBtn: boolean;
  modalHeaderTitle: string;
  modalButtonTitle: string;
  modalSeondaryButtonTitle?: string;
  modalButtonColor?: string;
  buttonCallback?: () => void;
  secondaryCustomAction?: () => void;
  isOnboardingError?: boolean;
  transactionPaymentError: boolean;
  showCartToastMsg: boolean;
  cartAddedItemType: CartItemType;
};

const MessageType = {
  NewOffers: 'NewOffers',
  OfferUnredeemed: 'OfferUnredeemed',
  OfferRedeemed: 'OfferRedeemed',
};

interface IProps {
  paymentCredentials: PaymentCredentials;
  canAddFunds: boolean;
  spinner: ISpinner;
  hideSpinner: () => void;
  endPaymentFunding: () => void;
}

Text.defaultProps = {
  ...(Text.defaultProps || {}),
  maxFontSizeMultiplier: 2,
};

class App extends React.Component<IProps, AppStateType> {
  handleAppStateChangeSubscription: NativeEventSubscription;
  private notificationHandlers: {
    foreground?: (event: NotificationWillDisplayEvent) => void;
    click?: (event: NotificationClickEvent) => void;
  } = {};
  private pendingNotificationNavigation: (() => void) | null = null;

  static setCurrentLockTime() {
    const lockRequiredTime = new Date().getTime() + 10 * 60 * 1000;
    PersistentStore.setLockTime(lockRequiredTime);
  }

  static getActiveRoute(
    navigationState: NavigationState,
  ): Record<string, string> | null {
    if (!navigationState) {
      return null;
    }

    const route = navigationState.routes[navigationState.index];

    if (route.state) {
      return App.getActiveRoute(route.state as NavigationState);
    }

    return route as unknown as Record<string, string>;
  }
  private logError(context: string, err: unknown) {
    const message = err instanceof Error ? err.message : JSON.stringify(err);
    Events.Error.trackEvent('Exception', context, message);
  }

  appState: string;
  showSpinnerTimeout: NodeJS.Timeout | null;
  backHandler: NativeEventSubscription;
  currentRoute: {[key: string]: string} | null;
  nestedPushAfterLogin: NestedPushAfterLogin | null = null;

  constructor(props: IProps) {
    super(props);
    this.appState = 'active';
    this.currentRoute = null;
    this.onSync = this.onSync.bind(this);
    this.showSpinner = this.showSpinner.bind(this);
    this.hideSpinner = this.hideSpinner.bind(this);
    this._handleAppStateChange = this._handleAppStateChange.bind(this);
    this.handleActiveState = this.handleActiveState.bind(this);
    this.onPasscodeScreen = this.onPasscodeScreen.bind(this);
    this.onNotificationReceived = this.onNotificationReceived.bind(this);
    this.onNotificationOpened = this.onNotificationOpened.bind(this);
    this.checkFirstLaunch = this.checkFirstLaunch.bind(this);
    this.checkIfLoggedIn = this.checkIfLoggedIn.bind(this);
    this.changeRootRoute = this.changeRootRoute.bind(this);
    this.resetFilterType = this.resetFilterType.bind(this);
    this.navStateChanged = this.navStateChanged.bind(this);
    this.showModal = this.showModal.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.hideModalAndNavigate = this.hideModalAndNavigate.bind(this);
    this.showToastMessage = this.showToastMessage.bind(this);
    this.state = {
      spinner: false,
      spinnerCallback: undefined,
      spinnerCancel: false,
      spinnerCancelText: '',
      spinnerText: '',
      showSuccessModal: false,
      modalDescription: '',
      modalDescriptionLine: 1,
      modalIsAcceptSnack: false,
      modalIsMultipleBtn: false,
      modalHeaderTitle: '',
      modalButtonTitle: '',
      modalSeondaryButtonTitle: '',
      modalButtonColor: '',
      showCartToastMsg: false,
      cartAddedItemType: CartItemType.Menu,
      screenProps: {
        state: {},
        actions: {
          showSpinner: this.showSpinner,
          hideSpinner: this.hideSpinner,
          showModal: this.showModal,
          hideModal: this.hideModal,
          hideModalAndNavigate: this.hideModalAndNavigate,
          changeRootRoute: this.changeRootRoute,
          resetFilterType: this.resetFilterType,
          navigateToFunding: (
            isConnected: boolean,
            params?: AccountFundingScreenProps,
          ) => {
            const processor =
              Settings.processors[this.props.paymentCredentials.type];
            const canAddFunds = this.props.canAddFunds;
            if (canAddFunds && processor && isConnected) {
              NavActions.navigate(AppRoutes.MyModal, {
                ...params,
                addFundsFailed: this.addFundFailedAction(
                  params?.addFundsFailed,
                ),
                addFundsValidate: params?.addFundsValidate,
                onClose: params?.onClose,
              });
            } else if (!processor) {
              alertError(
                Localized.Labels.account_instructions,
                '',
                undefined,
                Localized.Errors.funding_unavailable,
              );
            } else if (!canAddFunds) {
              confirm(
                Localized.Labels.in_order_to_reload_your_account,
                () => {
                  this.navigateToWalletScreen();
                },
                undefined,
                Localized.Labels.no_payment_method,
                Localized.Buttons.cancel,
                Localized.Buttons.go_to_payments,
              );
            }

            return;
          },
          showToast: this.showToastMessage,
        },
      },
      rootRoute: AppRoutes.Loading,
      transactionPaymentError: false,
    };
  }

  private oneSignalAdapter = new OneSignalAdapter((msg, err) =>
    this.logError(msg, err),
  );

  private _linkingSub: {remove?: () => void} | undefined;

  navigateToWalletScreen() {
    if (Settings.isNewUI()) {
      if (Settings.isCanteen()) {
        NavActions.push(AppRoutes.Wallet);
      } else {
        NavActions.push(AppRoutes.PDCards, {
          hideBackArrow: false,
        });
      }
    } else {
      NavActions.nestedPush(
        [AppRoutes.Main, AppRoutes.Settings],
        AppRoutes.Cards,
      );
    }
  }

  async componentDidMount() {
    FirebaseAnalytic.trackEvent('componentDidMount', 'App', {
      ...this.props,
      ...this.state,
    });
    const url = await Linking.getInitialURL();
    const isPasswordResetRedirect =
      url?.startsWith('tsfpay://open') &&
      !url.includes('code=') &&
      Platform.OS === 'android';

    if (isPasswordResetRedirect) {
      this.hideSpinner();
      this.props.hideSpinner?.();
      this.changeRootRoute(AppRoutes.Login);
      return;
    }

    try {
      if (url && handleOpenHomeDeepLink(url, this.changeRootRoute)) {
        return;
      }

      if (url && url.startsWith('tsfpay://order-tracking')) {
        this.changeRootRoute(AppRoutes.App);

        NavActions.resetSafe(AppRoutes.HomeTab);
      }

      this._linkingSub = Linking.addEventListener('url', ({url: inUrl}) => {
        if (inUrl && handleOpenHomeDeepLink(inUrl, this.changeRootRoute)) {
          return;
        }

        if (inUrl && inUrl.startsWith('tsfpay://order-tracking')) {
          this.changeRootRoute(AppRoutes.App);
          NavActions.resetSafe(AppRoutes.HomeTab);
        }
      });
    } catch (e) {
      this.logError('[Linking] failed to handle deep link', e);
    }

    this.oneSignalAdapter.onNotificationReceived = (notification) => {
      this.onNotificationReceived(notification);
    };

    this.oneSignalAdapter.onNotificationOpened = ({notification}) => {
      this.onNotificationOpened({notification});
    };

    await this.oneSignalAdapter.initialize();
    this.checkFirstLaunch();
    this.props.endPaymentFunding();
    try {
      await DBHelper.initDb();
    } catch (err) {
      this.logError('[DBHelper] initDb failed', err);
    }

    SyncHelper.addOnSyncListener(this.onSync);
    SyncHelper.addOnSyncFinishedListener(this.hideSpinner);

    this.handleAppStateChangeSubscription = AppState.addEventListener(
      'change',
      this._handleAppStateChange,
    );

    await migrateOldSessionIfNeeded();
    await tryRecoverSession(() => this.changeRootRoute(AppRoutes.Login));
    this.handleActiveState();

    this.backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      const handled =
        this.currentRoute &&
        this.currentRoute.routeName !== AppRoutes.PrivacyPolicy &&
        NavActions.pop();
      return handled;
    });

    let fallbackTimeout: NodeJS.Timeout | null = null;

    try {
      fallbackTimeout = setTimeout(() => {

        this.changeRootRoute(AppRoutes.Login);
      }, 10000);

      await AccountStore.waitForData();

      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }

      await this.checkIfLoggedIn();
    } catch (error) {
      this.logError('waitForData() error', error);
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      this.changeRootRoute(AppRoutes.Login);
    }

    try {
      const wasForceClosed =
        await AppLifecycleTracker.wasAppForceClosedWithCart();
      if (wasForceClosed) {
        AppLifecycleTracker.clearCartState();
      }
    } catch (err) {
      this.logError('[AppLifecycleTracker] cart check failed', err);
    }

    try {
      AppLifecycleTracker.startTracking();
    } catch (err) {
      this.logError('[AppLifecycleTracker] startTracking failed', err);
    }

    if (Platform.OS !== 'web') {
      try {
        const urlsAndEnv = await PersistentStore.getUrlsAndEnv();
        if (urlsAndEnv && urlsAndEnv.env) {
          Settings.env = urlsAndEnv.env;
        } else {

        }
      } catch (e) {
        this.logError('[App] Failed to load persisted env:', e);
      }
      await initializeInstana();
    }
  }

  componentWillUnmount() {
    this.handleAppStateChangeSubscription?.remove();

    try {
      this._linkingSub?.remove?.();
    } catch (e) {
      this.logError('[Linking] failed to remove subscription', e);
    }

    // Clean up OneSignal event listeners
    try {
      if (this.notificationHandlers.foreground) {
        OneSignal.Notifications.removeEventListener(
          'foregroundWillDisplay',
          this.notificationHandlers.foreground,
        );
      }
      if (this.notificationHandlers.click) {
        OneSignal.Notifications.removeEventListener(
          'click',
          this.notificationHandlers.click,
        );

      }
    } catch (err) {
      this.logError('[OneSignal] Failed to remove notification listeners', err);
    }
    this.notificationHandlers = {};

    if (!this.onPasscodeScreen()) {
      App.setCurrentLockTime();
    }

    if (this.backHandler) {
      this.backHandler.remove();
    }

    DBHelper.close();

    if (this.showSpinnerTimeout) {
      clearTimeout(this.showSpinnerTimeout);
    }
  }

  async showRewardTierUpgraded(openResult: Record<string, string>) {
    const rewardStatusId = openResult.rewardStatusId;
    PersistentStore.getRewardsResponse().then((storage: RewardStatusModel) => {
      if (storage) {
        const newRewardTier = getRewardProgramForTierStatus(
          rewardStatusId,
          storage.rewardStatuses,
        );
        if (
          newRewardTier &&
          this.getCurrentRoute() !== AppRoutes.RewardUpgradedPanel &&
          AccountStore.getAccountId()
        ) {
          NavActions.navigate(AppRoutes.RewardUpgradedPanel, {
            newStatusTier: newRewardTier.name,
            rewardStatuses: storage.rewardStatuses,
          });
        }
      }
    });
  }

  getCurrentRoute = () => {
    return NavActions.navigatorRef.getCurrentRoute().name;
  };

  async checkIfLoggedIn() {
    await authStore.migrateCredentials();
    const accountId = AccountStore.getAccountId();
    const firstName = AccountStore.getFirstName();

    if (accountId && accountId !== '-1' && firstName) {
      OneSignal.login(accountId);

      if (Platform.OS === 'web') {
        this.changeRootRoute(AppRoutes.App);
        NavActions.replace(AppRoutes.MainConsumer);
      } else {
        this.changeRootRoute(AppRoutes.App);
      }

      setTimeout(() => {
        if (this.pendingNotificationNavigation) {
          this.pendingNotificationNavigation();
          this.pendingNotificationNavigation = null;
        }
      }, 3000);
    } else {
      if (accountId) {
        await ActionsFactory.getAccountActions().logout();
      }
      this.changeRootRoute(AppRoutes.Login);
    }
  }

  async checkFirstLaunch() {
    try {
      const hasLaunched = await PersistentStore.hasLaunched();
      FirebaseAnalytic.trackEvent('checkFirstLaunch', 'App', {
        ...this.props,
        ...this.state,
        hasLaunched,
      });

      if (!hasLaunched) {
        Events?.FirstLaunch?.trackEvent();
        PersistentStore.setHasLaunched();
      }
    } catch (error) {

    }
  }

  navStateChanged(currentState: NavigationState) {
    const prevRoute = this.currentRoute;
    this.currentRoute = App.getActiveRoute(currentState);
    FirebaseAnalytic.trackEvent('navStateChanged', 'App', {
      ...this.props,
      ...this.state,
      currentState,
    });
    if (
      this.currentRoute &&
      prevRoute &&
      this.currentRoute.name !== prevRoute.name
    ) {
      Events.ScreenView.trackEvent(this.currentRoute.name);

      if (
        (this.currentRoute?.name === AppRoutes.Promotions ||
          this.currentRoute?.name === AppRoutes.NewHome) &&
        this.nestedPushAfterLogin
      ) {
        NavActions.nestedPush(
          this.nestedPushAfterLogin.nestedNames,
          this.nestedPushAfterLogin.name,
          this.nestedPushAfterLogin.params,
        );
        this.nestedPushAfterLogin = null;
      }
    }
  }

  onSync(message?: string) {
    const isDebug = AccountStore.isDebug();
    FirebaseAnalytic.trackEvent('navStateChanged', 'App', {
      ...this.props,
      ...this.state,
      message,
      isDebug,
    });
    if (isDebug) {
      this.showSpinner(
        message ?? '',
        true,
        function () {
          return;
        },
        Localized.Labels.dismiss,
      );
    }
  }

  onPasscodeScreen(): boolean {
    return this.state.rootRoute === AppRoutes.Passcode;
  }

  async onNotificationReceived(notification: OSNotification) {
    if (!notification || (!notification.title && !notification.body)) {
      return;
    }

    // Track notification event
    Events.NotificationReceived.trackEvent(
      notification as {
        payload?: {
          additionalData?: {
            fetchBalance?: boolean;
            transactionId?: boolean;
            transactionType?: boolean;
          };
          title?: string;
          body?: string;
        };
      },
    );

    const accountId = AccountStore.getAccountId();
    let data: {balance?: number} = {};
    if (notification && notification.additionalData) {
      data = notification.additionalData;
    }

    if (accountId && accountId !== '-1') {
      try {
        ActionsFactory.getAccountActions().reloadConsumerData({
          accountId,
          accountBalanceId: AccountStore.getAccountBalanceId(),
          email: AccountStore.getEmail(),
          newBalance: data.balance,
        });
      } catch (error) {
        this.logError('[OneSignal] reloadConsumerData failed', error);
      }
    }
    FirebaseAnalytic.trackEvent('onNotificationReceived', 'App', {
      ...this.props,
      ...this.state,
      data,
      notification,
      accountId,
    });
  }

  componentDidUpdate() {
    try {
      const accountId = AccountStore.getAccountId();
      const firstName = AccountStore.getFirstName();

      if (!accountId && !firstName) {
        if (this.state.rootRoute !== AppRoutes.Login) {
          this.changeRootRoute(AppRoutes.Login);
        }
      }
    } catch (error) {
      this.logError('componentDidUpdate: check account data failed', error);
    }
  }

  async onNotificationOpened(openResult: {notification: OSNotification}) {
    const accountId = AccountStore.getAccountId();
    FirebaseAnalytic.trackEvent('onNotificationOpened', 'App', {
      ...this.props,
      ...this.state,
      accountId,
      openResult,
    });

    try {
      const data = openResult.notification?.additionalData as
        | Record<string, any>
        | undefined;

      const isActivityTracker = !!(
        data &&
        (data.kdsStatus !== undefined ||
          data.orderId ||
          data.orderKind ||
          data.activityId ||
          data.liveActivity ||
          data.viewReceipt === true)
      );

      if (isActivityTracker) {
        NavActions.resetSafe(AppRoutes.HomeTab);
        return;
      }
    } catch (e) {
      this.logError('[NavDiag] activity tracker check failed', e);
    }

    const navigateToInbox = () =>
      NavActions.nestedPush([AppRoutes.HomeTab], AppRoutes.Inbox, {
        selectedTab: Localized.Labels.messages,
      });
    const handleNavigation = async () => {
      try {
        const data = openResult.notification?.additionalData as
          | Record<string, string>
          | undefined;
        if (data && accountId && accountId !== '-1') {
          if (data.transactionId && !data.rewardStatusId) {
            try {
              await ActionsFactory.getAccountActions().loadPurchaseHistory(
                accountId,
                1,
              );
              NavActions.navigate(AppRoutes.PurchaseHistoryDetail, {
                transactionId: data.transactionId,
                transactionType: data.transactionType,
              });
              Events.NotificationReceived.trackEvent(
                openResult as {
                  payload?: {
                    additionalData?: {
                      fetchBalance?: boolean;
                      transactionId?: boolean;
                      transactionType?: boolean;
                    };
                    title?: string;
                    body?: string;
                  };
                },
              );
            } catch (e) {

              navigateToInbox();
            }
            return;
          }

          if (data.snackId && data.locationId) {
            AccountStore.setOTSNotification(data.snackId, data.locationId);
            NavActions.navigate(AppRoutes.HomeTab);

            const onHomeReady = () => {
              AppNotificationsEventOpened.emit('triggerOTSNotification');
              AppNotificationsEventOpened.off('homeScreenReady', onHomeReady);
            };
            AppNotificationsEventOpened.on('homeScreenReady', onHomeReady);
            return;
          }

          if (data.rewardStatusId) {
            this.showRewardTierUpgraded(data);
            return;
          }

          if (data.messageType) {
            // Handling Connect & Pay deep-links
            switch (data.messageType) {
              case MessageType.NewOffers:
                NavActions.navigate(AppRoutes.Inbox);
                return;

              case MessageType.OfferUnredeemed: {
                const {screenProps} = this.state;
                screenProps.state.filterType = filterPositions.active;
                this.setState({screenProps});
                NavActions.navigate(AppRoutes.Inbox);
                return;
              }

              case MessageType.OfferRedeemed:
                NavActions.navigate(AppRoutes.PurchaseHistory);
                return;
            }
          }
          navigateToInbox();
        } else {
          navigateToInbox();
        }
      } catch (e) {

      }
      navigateToInbox();
    };

    if (this.state.rootRoute !== AppRoutes.App) {
      this.pendingNotificationNavigation = handleNavigation;
    } else {
      handleNavigation();
    }
  }

  _handleAppStateChange(currentAppState: string) {
    FirebaseAnalytic.trackEvent('_handleAppStateChange', 'App', {
      ...this.props,
      ...this.state,
      currentAppState,
    });
    if (currentAppState !== this.appState) {
      this.appState = currentAppState;

      if (currentAppState === 'active') {
        this.handleActiveState();
      } else if (!this.onPasscodeScreen()) {
        App.setCurrentLockTime();
      }
    }
  }

  changeRootRoute(
    route: RootRoutes,
    initialTabRoute?: AppRoutes,
    nestedPushAfterLogin?: NestedPushAfterLogin | null,
  ) {
    FirebaseAnalytic.trackEvent('changeRootRoute', 'App', {
      ...this.props,
      ...this.state,
      route,
      initialTabRoute,
      nestedPushAfterLogin,
    });
    this.nestedPushAfterLogin = nestedPushAfterLogin ?? null;
    const {screenProps} = this.state;
    screenProps.state.initialTabRoute = initialTabRoute;
    this.setState({
      rootRoute: route,
      screenProps,
    });
  }

  resetFilterType() {
    const {screenProps} = this.state;

    screenProps.state.filterType = filterPositions.all;

    this.setState({screenProps}, () => {
      FirebaseAnalytic.trackEvent('resetFilterType', 'App', {
        ...this.props,
        ...this.state,
      });
    });
  }

  showSpinner(
    spinnerText = '',
    cancel = false,
    cancelCallback: () => void,
    cancelText: string = Localized.Buttons.cancel,
  ) {
    this.setState(
      {
        spinner: true,
        spinnerCancel: cancel,
        spinnerCallback: cancelCallback,
        spinnerCancelText: cancelText,
        spinnerText,
      },
      () => {
        FirebaseAnalytic.trackEvent('showSpinner', 'App', {
          ...this.props,
          ...this.state,
        });
      },
    );

    if (this.showSpinnerTimeout) {
      clearTimeout(this.showSpinnerTimeout);
    }

    this.showSpinnerTimeout = setTimeout(() => {
      this.hideSpinner();
    }, 60 * 1000);
  }

  hideSpinner() {
    this.setState(
      {
        spinner: false,
        spinnerText: '',
        spinnerCancel: false,
        spinnerCallback: undefined,
        spinnerCancelText: '',
      },
      () => {
        FirebaseAnalytic.trackEvent('hideSpinner', 'App', {
          ...this.props,
          ...this.state,
        });
      },
    );

    if (this.showSpinnerTimeout) {
      clearTimeout(this.showSpinnerTimeout);
    }

    this.showSpinnerTimeout = null;
  }
  showModal(
    description: string,
    headerTitle: string,
    buttonTitle: string,
    buttonColor: string = Styles.primaryColor,
    descLines = 1,
    isAcceptSnack = false,
    isMultipleBtn = false,
    seondaryButtonTitle: string,
    buttonCallback?: () => void,
    secondaryCustomAction?: () => void,
    isOnboardingError?: boolean,
  ) {
    this.setState({
      showSuccessModal: true,
      modalDescription: description,
      modalDescriptionLine: descLines || 1,
      modalIsAcceptSnack: isAcceptSnack,
      modalIsMultipleBtn: isMultipleBtn,
      modalHeaderTitle: headerTitle,
      modalButtonTitle: buttonTitle,
      modalSeondaryButtonTitle: seondaryButtonTitle,
      modalButtonColor: buttonColor,
      buttonCallback: buttonCallback,
      secondaryCustomAction: secondaryCustomAction,
      isOnboardingError: isOnboardingError,
    });
  }
  hideModal() {
    this.setState({
      showSuccessModal: false,
    });
  }
  hideModalAndNavigate() {
    this.setState(
      {
        showSuccessModal: false,
      },
      () => {
        NavActions.pop();
      },
    );
  }

  async handleActiveState() {
    try {
      const currentRouteName = this.currentRoute
        ? this.currentRoute.name
        : 'No Route';

      if (
        currentRouteName === AppRoutes.CreditCardAdd ||
        currentRouteName === AppRoutes.PDCards ||
        currentRouteName === AppRoutes.AccountSetup
      ) {
        return;
      }

      SyncHelper.performSyncWithServer();
      const accountId = await PersistentStore.getAccountId();
      FirebaseAnalytic.trackEvent('handleActiveState', 'App', {
        ...this.props,
        ...this.state,
        accountId,
      });

      if (accountId && accountId !== '-1') {
        try {
          const [lockRequiredTime] = await Promise.all([
            PersistentStore.getLockTime(),
            AccountStore.waitForData(),
          ]);
          const currentTime = new Date().getTime();

          if (
            lockRequiredTime &&
            AccountStore.getPinRequired() &&
            currentTime > lockRequiredTime
          ) {
            this.setState({
              spinner: false,
              rootRoute: AppRoutes.Passcode,
            });
          } else {
            App.setCurrentLockTime();
          }
        } catch (error) {

        }
      }
    } catch (error) {

    }
  }

  paymentFailed = (error: any) => {
    if (!paymentIsCancelled(error)) {
      this.setState({transactionPaymentError: true});
    }
  };

  closePaymentError = (navigateToCards = false) => {
    this.setState({transactionPaymentError: false});
    if (navigateToCards) {
      Platform.OS === 'web'
        ? NavActions.nestedPush(
            [AppRoutes.Main, AppRoutes.Account],
            AppRoutes.PDCards,
          )
        : this.navigateToWalletScreen();
    }
  };

  renderTransactionPaymentProcessError = () => {
    return (
      this.state.transactionPaymentError && (
        <ErrorModal
          isModalVisible={this.state.transactionPaymentError}
          headerTitle={Localized.Labels.something_went_wrong}
          description={Localized.Labels.transaction_payment_error_description}
          button1Title={Localized.Buttons.verify_credit_card_details}
          button2Title={Localized.Buttons.cancel}
          onModalClose={() => this.closePaymentError()}
          button1Action={() => this.closePaymentError(true)}
          button2Action={() => this.closePaymentError()}
          button1TextStyle={styles.errorModalButtonText}
        />
      )
    );
  };

  addFundFailedAction = (addFundsFailed?: () => void) => {
    if (addFundsFailed) {
      return addFundsFailed;
    } else {
      return this.paymentFailed;
    }
  };

  showToastMessage(cartMsgType: CartItemType) {
    this.setState(
      {
        cartAddedItemType: cartMsgType,
        showCartToastMsg: true,
      },
      () => this.resetToastMessage(),
    );
  }

  async resetToastMessage() {
    await Util.delay(3000);
    this.setState({
      showCartToastMsg: false,
    });
  }

  render() {
    const {spinner} = this.props;
    const {statusBarStyleDescriptor, statusBarBackgroundColorDescriptor} =
      getDescriber();
    return (
      <GestureHandlerRootView style={Styles.Style.flex}>
        <StatusBar
          barStyle={statusBarStyleDescriptor()}
          backgroundColor={statusBarBackgroundColorDescriptor()}
          translucent={
            Settings.buildType === BuildTypeConstants.default ||
            Settings.buildType === BuildTypeConstants.refive
          }
        />
        <SafeAreaProvider>
          <ScreenContext.Provider value={this.state.screenProps}>
            <AppNavigator
              onStateChange={this.navStateChanged}
              rootRoute={this.state.rootRoute}
            />
          </ScreenContext.Provider>
        </SafeAreaProvider>
        {this.state.spinner && (
          <ModalSpinner
            text={this.state.spinnerText}
            cancel={this.state.spinnerCancel}
            cancelText={this.state.spinnerCancelText}
            callback={() => {
              this.hideSpinner();

              if (this.state.spinnerCallback) {
                this.state.spinnerCallback();
              }
            }}
          />
        )}
        {spinner.display && (
          <ModalSpinner
            text={spinner.spinnerText || ''}
            cancel={spinner.spinnerCancel || false}
            cancelText={spinner.spinnerCancelText || ''}
            callback={() => {
              hideSpinner();

              if (spinner.spinnerCallback) {
                spinner.spinnerCallback();
              }
            }}
          />
        )}
        {this.state.showSuccessModal && (
          <SuccessModal
            isModalVisible={this.state.showSuccessModal}
            headerTitle={this.state.modalHeaderTitle}
            description={this.state.modalDescription}
            buttonTitle={this.state.modalButtonTitle}
            seondaryButtonTitle={this.state.modalSeondaryButtonTitle}
            buttonColor={this.state.modalButtonColor}
            descLines={this.state.modalDescriptionLine}
            isAcceptSnack={this.state.modalIsAcceptSnack}
            isMultipleBtn={this.state.modalIsMultipleBtn}
            secondaryCustomAction={this.state.secondaryCustomAction}
            isOnboardingError={this.state.isOnboardingError}
            onModalClose={() => {
              this.hideModal();
              if (this.state.buttonCallback) {
                this.state.buttonCallback();
              }
            }}
            onModalCloseAndNavigate={() => {
              this.hideModalAndNavigate();
              if (this.state.buttonCallback) {
                this.state.buttonCallback();
              }
            }}
          />
        )}
        {this.renderTransactionPaymentProcessError()}
        {this.state.showCartToastMsg && (
          <CartToastView
            cartItemType={this.state.cartAddedItemType}
            showMessage={this.state.showCartToastMsg}
          />
        )}
      </GestureHandlerRootView>
    );
  }
}

const styles = StyleSheet.create({
  errorModalButtonText: {
    textTransform: 'none',
  },
});

export default connect(
  (state: RootState) => ({
    spinner: state.screen.spinner,
    paymentCredentials: state.account.paymentCredentials,
    canAddFunds: state.account.canAddFunds,
  }),
  (dispatch: AppDispatch) => ({
    hideSpinner: () => dispatch(hideSpinner()),
    endPaymentFunding: () => dispatch(endPaymentFunding()),
  }),
)(App);
